import { useCallback, useEffect, useRef, useState, type Dispatch } from "react";

import { Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { loadStripe, type Stripe as StripeJs } from "@stripe/stripe-js";
import { m } from "framer-motion";
import { AlertCircle, CheckCircle2, Loader2 } from "lucide-react";

import {
  GoToServicePauseButton,
  isServicesPausedError,
  useServicesPausedBillingMessage,
} from "@/components/billing/services-paused-billing";
import { MAX_CLINIC_DOCTORS } from "@/components/pricing/clinic-seat-pricing";
import { Button } from "@/components/ui/button";
import { isVaultUnlockCancelled, useProtectedAction } from "@/lib/hooks/use-protected-action";

import type { CheckoutAction, CheckoutState } from "./checkout-types";

// Source: legacy src/components/billing/checkout/checkout-step-pay.tsx (1:1
// port). Differences vs legacy, per SPA conventions: next-intl → Lingui
// macros (the legacy en.json values are the msgids), raw fetch() → apiFetch
// (ApiError carries the legacy `!res.ok` body, so `body.error || generic`
// behaves identically), motion.* → m.*, and the publishable-key fallback is
// import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY instead of
// process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY (same source as
// stripe-elements-provider.tsx).
//
// VAULT PIN (2026-10): POST /api/checkout/charge-saved-card, and
// /api/checkout/create when it would spend wallet credit (or credit an earlier
// attempt parked on Stripe), answer 403 vault_locked while a PIN guarding
// "billing" or "wallet" is not unlocked. Both calls go through runProtected:
// the PIN gate opens and, once unlocked, the call runs once more. Dismissing
// it lands on the error state, whose Try again re-asks.

// ---------------------------------------------------------------------------
// Stripe instance cache (shared across mounts)
// ---------------------------------------------------------------------------

const stripeCache = new Map<string, Promise<StripeJs | null>>();

// Named for the message placeholder ({MAX_DOCTORS}) shared with the catalogs.
const MAX_DOCTORS = MAX_CLINIC_DOCTORS;
function getStripe(publishableKey: string): Promise<StripeJs | null> {
  if (!stripeCache.has(publishableKey)) {
    stripeCache.set(publishableKey, loadStripe(publishableKey));
  }
  return stripeCache.get(publishableKey)!;
}

// ---------------------------------------------------------------------------
// API response shapes (legacy read untyped res.json(); mirrors
// apps/api/src/routes/checkout.ts)
// ---------------------------------------------------------------------------

interface ChargeSavedCardResponse {
  success?: boolean;
  requiresAction?: boolean;
  clientSecret?: string;
  publishableKey?: string;
  subscriptionId?: string | null;
  stripeSubscriptionId?: string;
}

interface CreateCheckoutSessionResponse {
  walletPaid?: boolean;
  subscriptionId?: string | null;
  redirectUrl?: string;
  clientSecret?: string;
  publishableKey?: string;
  sessionId?: string;
  sessionUrl?: string;
}

interface ConfirmCheckoutResponse {
  message?: string;
  subscriptionId?: string | null;
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface CheckoutStepPayProps {
  state: CheckoutState;
  dispatch: Dispatch<CheckoutAction>;
  onComplete: () => void;
  planCode: string;
  interval: "monthly" | "annual";
  entityType: "provider" | "facility" | "business" | "user";
  entityId?: string;
  quantity: number;
  /** Pack counts keyed by featureKey — posted verbatim to /api/checkout/create. */
  addons: { packs: Record<string, number> };
  selectedProfileId: string | null;
  locale: string;
  countryCode: string;
  /** 409 SERVICES_PAUSED: closes the checkout and shows the pause card (billing page only). */
  onShowServicePause?: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CheckoutStepPay({
  state,
  dispatch,
  onComplete,
  planCode,
  interval,
  entityType,
  entityId,
  quantity,
  addons,
  selectedProfileId,
  locale,
  countryCode,
  onShowServicePause,
}: CheckoutStepPayProps) {
  const { t } = useLingui();
  const initiatedRef = useRef(false);
  const servicesPausedMessage = useServicesPausedBillingMessage();
  // The last refusal was SERVICES_PAUSED — the error state offers the way out.
  const [servicesPaused, setServicesPaused] = useState(false);
  // The purchase refusals with a code (apps/api checkout.ts).
  const purchaseErrorCopy = useCallback(
    (code: string | undefined): string | null =>
      code === "SERVICES_PAUSED"
        ? servicesPausedMessage
        : code === "subscription_exists"
          ? t`This profile already has an active plan of this kind. Change it from your billing page instead of buying a second one.`
          : code === "checkout_in_progress"
            ? t`A purchase is already in progress. Please wait a moment.`
            : // Settling: the same purchase, never bought again (the request id stays).
              code === "payment_processing"
              ? t`Your payment is being processed. Your plan starts as soon as it clears.`
              : // The plan and entity refusals /billing/subscribe used to translate
                // before it paid through this modal.
                code === "plan_misconfigured"
                ? t`This plan is not set up for per-doctor billing yet. Please contact support.`
                : code === "plan_not_purchasable"
                  ? t`This plan cannot be bought right now.`
                  : code === "annual_not_available_for_coverage"
                    ? t`Clinic coverage is billed monthly — annual is not available.`
                    : code === "no_facility"
                      ? t`You do not manage a clinic yet.`
                      : code === "no_business"
                        ? t`You do not manage a business yet.`
                        : code === "entity_not_owned"
                          ? t`You do not have a billing role at that workspace.`
                          : code === "invalid_quantity"
                            ? t`Pick a whole number of doctors between 1 and ${MAX_DOCTORS}.`
                            : null,
    [t, servicesPausedMessage],
  );
  // One id per purchase attempt (this step's lifetime): a resend of the same
  // charge carries it, and the API answers with the same Stripe subscription
  // instead of charging the card a second time.
  const requestIdRef = useRef(globalThis.crypto.randomUUID());
  const { runProtected, PinGateElement } = useProtectedAction(["billing", "wallet"]);

  // Local state for Stripe Embedded Checkout
  const [stripePromise, setStripePromise] = useState<Promise<StripeJs | null> | null>(null);
  const [embeddedSecret, setEmbeddedSecret] = useState<string | null>(null);
  const [status, setStatus] = useState<
    "idle" | "charging" | "3ds" | "creating-session" | "embedded" | "success" | "error"
  >("idle");

  // --- Determine payment path on mount ---
  useEffect(() => {
    if (initiatedRef.current) return;
    initiatedRef.current = true;

    if (state.selectedCardId) {
      // Path A: Charge saved card
      chargeSavedCard();
    } else if (state.clientSecret) {
      // Path B: Client secret already provided (pre-created session)
      setupEmbedded(state.clientSecret);
    } else {
      // Path C: Create new Stripe Embedded Checkout session
      createCheckoutSession();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Path A: Charge saved card ---
  const chargeSavedCard = useCallback(async () => {
    setStatus("charging");
    dispatch({ type: "SET_CARD_LOADING", loading: true });
    dispatch({ type: "SET_ERROR", error: null });

    try {
      const data = await runProtected(() =>
        apiFetch.post<ChargeSavedCardResponse>("/api/checkout/charge-saved-card", {
          planCode,
          interval,
          entityType,
          entityId,
          quantity,
          addons,
          billingProfileId: selectedProfileId,
          paymentMethodId: state.selectedCardId,
          authorizeRecurring: state.authorizeRecurring,
          countryCode,
          requestId: requestIdRef.current,
        }),
      );

      // 3D Secure required
      if (data.requiresAction && data.clientSecret) {
        setStatus("3ds");
        dispatch({ type: "SET_3DS_LOADING", loading: true });

        try {
          const stripeInstance = data.publishableKey ? await getStripe(data.publishableKey) : null;
          if (!stripeInstance) {
            dispatch({
              type: "SET_ERROR",
              error: t`Something went wrong. Please try again.`,
            });
            setStatus("error");
            return;
          }

          const { error: confirmError } = await stripeInstance.confirmCardPayment(
            data.clientSecret,
          );

          if (confirmError) {
            // Not confirmed: the held purchase is cancelled, and the next
            // attempt is a new purchase (a new request id), not this one again.
            await apiFetch
              .post("/api/checkout/charge-saved-card/abandon", {
                stripeSubscriptionId: data.subscriptionId,
              })
              .catch(() => undefined);
            requestIdRef.current = globalThis.crypto.randomUUID();
            dispatch({
              type: "SET_ERROR",
              error: confirmError.message || t`Something went wrong. Please try again.`,
            });
            setStatus("error");
            return;
          }

          // 3DS succeeded — confirm DB records (legacy didn't check the
          // response; failures are non-critical — webhook handles it in prod)
          try {
            await apiFetch.post("/api/checkout/charge-saved-card/confirm-3ds", {
              stripeSubscriptionId: data.subscriptionId,
            });
          } catch {
            // Non-critical — payment already succeeded at Stripe
          }
        } finally {
          dispatch({ type: "SET_3DS_LOADING", loading: false });
        }
      }

      // Payment succeeded
      dispatch({
        type: "PAYMENT_SUCCESS",
        subscriptionId: data.subscriptionId || null,
      });
      setStatus("success");
      onComplete();
    } catch (err) {
      if (isVaultUnlockCancelled(err)) {
        dispatch({ type: "SET_ERROR", error: t`Enter your PIN to complete the payment.` });
      } else if (err instanceof ApiError) {
        setServicesPaused(isServicesPausedError(err));
        // A decline is definitive for this purchase: the next try with another
        // card is a new one (a new request id), not a replay of the decline.
        if (err.status === 402) requestIdRef.current = globalThis.crypto.randomUUID();
        const body = err.body as { error?: string; code?: string } | null;
        dispatch({
          type: "SET_ERROR",
          error:
            purchaseErrorCopy(body?.code) ??
            (body?.error || t`Something went wrong. Please try again.`),
        });
      } else {
        dispatch({ type: "SET_ERROR", error: t`Something went wrong. Please try again.` });
      }
      setStatus("error");
    } finally {
      dispatch({ type: "SET_CARD_LOADING", loading: false });
    }
  }, [
    planCode,
    interval,
    entityType,
    entityId,
    quantity,
    addons,
    selectedProfileId,
    countryCode,
    state.selectedCardId,
    state.authorizeRecurring,
    dispatch,
    onComplete,
    runProtected,
    purchaseErrorCopy,
    t,
  ]);

  // --- Path C: Create Stripe Embedded Checkout session ---
  // --- Setup Stripe Embedded Checkout ---
  const setupEmbedded = useCallback((secret: string, publishableKey?: string) => {
    const key =
      publishableKey || (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined);
    if (key) {
      setStripePromise(getStripe(key));
    }
    setEmbeddedSecret(secret);
    setStatus("embedded");
  }, []);

  const createCheckoutSession = useCallback(async () => {
    setStatus("creating-session");
    dispatch({ type: "SET_LOADING", loading: true });
    dispatch({ type: "SET_ERROR", error: null });

    try {
      const data = await runProtected(() =>
        apiFetch.post<CreateCheckoutSessionResponse>("/api/checkout/create", {
          planCode,
          interval,
          entityType,
          entityId,
          useWalletCredits: true,
          embedded: true,
          quantity,
          addons,
          billingProfileId: selectedProfileId,
          locale,
          countryCode,
        }),
      );

      // Wallet fully covered
      if (data.walletPaid) {
        dispatch({
          type: "PAYMENT_SUCCESS",
          subscriptionId: data.subscriptionId || null,
        });
        setStatus("success");
        onComplete();
        return;
      }

      // Embedded checkout
      if (data.clientSecret) {
        dispatch({ type: "SET_CLIENT_SECRET", clientSecret: data.clientSecret });
        setupEmbedded(data.clientSecret, data.publishableKey);
        return;
      }

      // Hosted checkout redirect (fallback)
      if (data.sessionUrl) {
        window.location.href = data.sessionUrl;
        return;
      }

      dispatch({ type: "SET_ERROR", error: t`Something went wrong. Please try again.` });
      setStatus("error");
    } catch (err) {
      if (isVaultUnlockCancelled(err)) {
        dispatch({ type: "SET_ERROR", error: t`Enter your PIN to complete the payment.` });
      } else if (err instanceof ApiError) {
        setServicesPaused(isServicesPausedError(err));
        const body = err.body as { error?: string; code?: string } | null;
        dispatch({
          type: "SET_ERROR",
          error:
            err.status === 503
              ? t`Payment system is being set up. Please try again later.`
              : (purchaseErrorCopy(body?.code) ??
                (body?.error || t`Something went wrong. Please try again.`)),
        });
      } else {
        dispatch({ type: "SET_ERROR", error: t`Something went wrong. Please try again.` });
      }
      setStatus("error");
    } finally {
      dispatch({ type: "SET_LOADING", loading: false });
    }
  }, [
    planCode,
    interval,
    entityType,
    entityId,
    quantity,
    addons,
    selectedProfileId,
    locale,
    countryCode,
    dispatch,
    onComplete,
    runProtected,
    purchaseErrorCopy,
    setupEmbedded,
    t,
  ]);

  // --- Embedded checkout complete handler ---
  const handleEmbeddedComplete = useCallback(async () => {
    // Dev only (webhooks cannot reach localhost): record this session at once.
    // /confirm refuses outside dev, and without the session id it would pick
    // the latest one. A client secret is "<session id>_secret_<…>".
    let subId: string | null = null;
    if (import.meta.env.DEV && embeddedSecret) {
      try {
        const data = await apiFetch.post<ConfirmCheckoutResponse>("/api/checkout/confirm", {
          sessionId: embeddedSecret.split("_secret_")[0],
        });
        subId = data.subscriptionId || null;
      } catch {
        // Non-critical: the webhook records it.
      }
    }
    dispatch({ type: "PAYMENT_SUCCESS", subscriptionId: subId });
    setStatus("success");
    onComplete();
  }, [dispatch, onComplete, embeddedSecret]);

  // --- Retry ---
  const handleRetry = useCallback(() => {
    initiatedRef.current = false;
    setServicesPaused(false);
    dispatch({ type: "SET_ERROR", error: null });
    setStatus("idle");
    // Re-trigger
    if (state.selectedCardId) {
      chargeSavedCard();
    } else {
      createCheckoutSession();
    }
  }, [state.selectedCardId, chargeSavedCard, createCheckoutSession, dispatch]);

  // Stripe.js deprecated `clientSecret` for `fetchClientSecret`. The session
  // already exists by the time the provider mounts (/api/checkout/create has
  // to rule out wallet payment and the PIN gate first), so this only hands it
  // over. Stripe reads it once per provider: memoised on the secret.
  const fetchEmbeddedSecret = useCallback(async () => embeddedSecret ?? "", [embeddedSecret]);

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  // Loading / processing states
  if (
    status === "idle" ||
    status === "charging" ||
    status === "3ds" ||
    status === "creating-session"
  ) {
    return (
      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center justify-center gap-4 py-20"
      >
        <Loader2 className="text-primary h-8 w-8 animate-spin" />
        <p className="text-muted-foreground text-sm">
          {status === "3ds"
            ? t`Completing bank verification...`
            : status === "charging"
              ? t`Processing payment...`
              : t`Preparing checkout...`}
        </p>
        {/* The PIN gate only opens while a call is in flight — this state. */}
        {PinGateElement}
      </m.div>
    );
  }

  // Error state
  if (status === "error") {
    return (
      <m.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center justify-center gap-4 px-6 py-16"
      >
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-100 dark:bg-red-950/50">
          <AlertCircle className="h-7 w-7 text-red-600" />
        </div>
        <p className="text-foreground text-center text-sm font-medium">
          {state.error || t`Something went wrong. Please try again.`}
        </p>
        {servicesPaused && onShowServicePause ? (
          <GoToServicePauseButton onClick={onShowServicePause} />
        ) : (
          <Button variant="outline" onClick={handleRetry}>
            <Trans>Try again</Trans>
          </Button>
        )}
      </m.div>
    );
  }

  // Success (brief flash before onComplete navigates away)
  if (status === "success") {
    return (
      <m.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex flex-col items-center justify-center gap-3 py-20"
      >
        <CheckCircle2 className="h-10 w-10 text-emerald-600" />
        <p className="text-foreground text-sm font-medium">
          <Trans>Thank you for your purchase!</Trans>
        </p>
      </m.div>
    );
  }

  // Stripe Embedded Checkout
  if (status === "embedded" && embeddedSecret && stripePromise) {
    return (
      <m.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        transition={{ duration: 0.2 }}
        className="min-h-[400px] sm:py-4 [&_.embedded-checkout-inner-container]:!rounded-none [&_.embedded-checkout-inner-container]:!border-0 [&_.embedded-checkout-inner-container]:!shadow-none [&_iframe]:!border-0"
      >
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{
            fetchClientSecret: fetchEmbeddedSecret,
            // Card and wallet payments end here (the session is created with
            // redirect_on_completion "if_required"); Stripe keeps the first
            // onComplete it is given, so it must stay stable.
            onComplete: handleEmbeddedComplete,
          }}
        >
          <EmbeddedCheckout className="!border-0 !shadow-none" />
        </EmbeddedCheckoutProvider>
      </m.div>
    );
  }

  // Fallback loading
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-20">
      <Loader2 className="text-primary h-8 w-8 animate-spin" />
      <p className="text-muted-foreground text-sm">
        <Trans>Preparing checkout...</Trans>
      </p>
    </div>
  );
}
